import React, { forwardRef, Fragment, memo, useState } from "react";
import clsx from "clsx";
import styles from "./Powertrain.module.scss";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import HybridLabel from "../../../../../../shared-components/css-modules/lexus/molecules/HybridLabel";
import Badge from "../../../../../../shared-components/css-modules/lexus/atoms/Badge";
import { CarConfigConfigureSectionsLbx } from "../../../../../utils/types/SectionTypesLbx";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import type { AtmosphereWheeldriveType } from "../../../../../utils/types/AtmosphereType";
import useAphLbxPowertrain from "../../../../../hooks/lbx/useAphLbxPowertrain";
import Price from "../../../common/price/Price";
import { useCarConfigSelector } from "../../../../../redux/store";
import { selectCarMotorization } from "../../../../../redux/slices/CarConfigCarSlice";
import SidePanel from "../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import EngineDetails from "../../../configure/sections/engine/EngineDetails";
import { getEcoTagType } from "../../../../../../../shared-logic/utils/typedUtils";
import {
    getEcoBadgeType,
    getWLTPLabel,
    hasAlternativeEcoSpecFlow,
} from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import Popover from "../../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import a11y from "../../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { trackPowertrainLearnMore } from "../../../../../utils/tracking";
import EcoLabel from "../../../../../../shared-components/css-modules/lexus/molecules/EcoLabel";
import EfficiencyClass from "../../../common/eco/EfficiencyClass";
import { getEcoBadgeUrl } from "../../../../../../../../common-deprecated/utils";
import { getMotorizationLabelConfig } from "../../../../../utils/motorizationUtils";
import { ecoTagToLabel } from "../../../../../../../../common-deprecated/utils/ecoTagUtils";
import { CarConfigLabel, getCarConfigLabel } from "../../../../../utils/labelUtils";

export type Props = {
    wheeldrive: AtmosphereWheeldriveType;
};

const Wheeldrive = forwardRef<HTMLElement, Props>((props, ref): JSX.Element | null => {
    const { wheeldrive } = props;
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const currentMotorization = useCarConfigSelector(selectCarMotorization);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const powertrain = useAphLbxPowertrain(wheeldrive);

    if (!powertrain) return null;
    const { motorization, notAvailableInCurrentPacks, selectWheeldrive } = powertrain;

    if (!motorization) return null;

    const alternativeEcoSpecFlow = hasAlternativeEcoSpecFlow(commonSettings);
    const ecoTag = getEcoTagType({
        id: motorization.engine.id,
        name: motorization.engine.name,
        hybrid: motorization.engine.type.hybrid,
        electric: motorization.engine.type.electric,
        fuelCell: motorization.engine.type.fuelCell,
        plugin: motorization.engine.type.plugin,
        internalCode: motorization.engine.internalCode,
        type: motorization.engine.type.name,
        typeId: motorization.engine.type.id,
        typeCode: motorization.engine.type.internalCode,
    });

    const efficiencyLabelConfig = getMotorizationLabelConfig(motorization, alternativeEcoSpecFlow);
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, motorization.engine.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);
    const ecoTagLabel = ecoTagToLabel(ecoTag);

    const isActive =
        currentMotorization.engine.id === motorization.engine.id &&
        currentMotorization.wheeldrive.id === motorization.wheeldrive.id &&
        currentMotorization.transmission.id === motorization.transmission.id;

    const handleClick = (target: string): void => {
        scrollToTarget(target);
    };

    return (
        <>
            <article
                className={clsx(styles.engine, isActive && styles.active, notAvailableInCurrentPacks && styles.upgrade)}
                ref={ref}
            >
                {notAvailableInCurrentPacks && (
                    <div className={styles["engine-badge-holder"]}>
                        {wheeldrive.packs.length > 0 && (
                            <Badge variant="sky">
                                {/*Other translations have been provided*/}
                                <CarConfigLabel
                                    useHTML
                                    label="carConfigLbxWheeldriveIncludedPacks"
                                    params={{
                                        packs: wheeldrive.packs.map((includedPack) => includedPack.name).toString(),
                                    }}
                                />
                            </Badge>
                        )}
                    </div>
                )}
                <div>
                    <div className={styles["engine-label"]}>
                        {ecoBadgeUrl && (
                            <div className={styles.ecolabel}>
                                <EcoLabel url={ecoBadgeUrl} />
                            </div>
                        )}
                        {efficiencyLabelConfig.show && (
                            <div className={styles.efficiency}>
                                <EfficiencyClass label={efficiencyLabelConfig.label} />
                            </div>
                        )}
                        {ecoTagLabel && (
                            <HybridLabel
                                variant={ecoTag}
                                label={getCarConfigLabel({ commonSettings, carConfigSettings }, ecoTagLabel)}
                                transparent
                            />
                        )}
                    </div>
                    <header className={styles["engine-header"]}>
                        <Text tag="h3" variant="apheleia-h4" className={styles["engine-title"]}>
                            {motorization?.wheeldrive.internalCode === "2WD" ? (
                                <CarConfigLabel label="carConfigLbxGradeWheeldriveFront" />
                            ) : (
                                <CarConfigLabel label="carConfigLbxGradeWheeldriveAll" />
                            )}
                        </Text>

                        <Text tag="div" variant="apheleia-body" className={styles.price}>
                            <Price price={motorization.price} />
                        </Text>
                    </header>
                </div>
                <div
                    className={styles["engine-description"]}
                    dangerouslySetInnerHTML={{ __html: motorization.wheeldrive.description }}
                />
                {motorization.specs.length > 0 && (
                    <div className={styles["engine-info"]}>
                        <dl>
                            {motorization.specs.slice(0, 2).map((spec) => (
                                <Fragment key={`${spec.id}-${spec.name}`}>
                                    <dt>{spec.name}</dt>
                                    <dd>
                                        {spec.value}
                                        {spec.unit}
                                    </dd>
                                </Fragment>
                            ))}
                        </dl>

                        <dl>
                            {motorization.specs.slice(3, 5).map((spec) => {
                                const wltpDisclaimer = getWLTPLabel(
                                    commonSettings,
                                    spec.certification,
                                    motorization.engine.type.electric,
                                );

                                return (
                                    <Fragment key={`${spec.id}-${spec.name}`}>
                                        <dt>{spec.name}</dt>
                                        <dd>
                                            {spec.value}
                                            {spec.unit}
                                        </dd>
                                        <dt>
                                            <Popover placement="top">
                                                <Popover.Trigger>
                                                    <Button variant="clean">
                                                        <Icon variant="info" sizing="xs" aria-hidden="true" />
                                                        <span className={a11y["sr-only"]}>
                                                            <CarConfigLabel label="carConfigA11yButtonToggleCo2Info" />
                                                        </span>
                                                    </Button>
                                                </Popover.Trigger>
                                                <Popover.Content>
                                                    <div dangerouslySetInnerHTML={{ __html: wltpDisclaimer }} />
                                                </Popover.Content>
                                            </Popover>
                                        </dt>
                                    </Fragment>
                                );
                            })}
                        </dl>
                    </div>
                )}
                <footer className={styles["engine-footer"]}>
                    <Button
                        variant="btn-text-upper"
                        onClick={() => {
                            trackPowertrainLearnMore(motorization.engine.type);
                            setShowDetails(true);
                        }}
                    >
                        <CarConfigLabel label="carConfigEngineLearnMore" />
                    </Button>
                    {notAvailableInCurrentPacks ? (
                        <Button
                            variant="btn-default"
                            sizing="full-width"
                            onClick={() => handleClick(CarConfigConfigureSectionsLbx.Packs)}
                        >
                            <CarConfigLabel label="carConfigLbxUpgradePack" />
                            <Icon variant="arrow-right" sizing="md" aria-hidden="true" />
                        </Button>
                    ) : (
                        <Button
                            variant="btn-lbx"
                            disabled={isActive}
                            sizing="full-width"
                            className={styles["engine-button"]}
                            onClick={selectWheeldrive}
                        >
                            <CarConfigLabel label={isActive ? "carConfigLbxSelected" : "carConfigLbxSelect"} />
                        </Button>
                    )}
                </footer>
            </article>
            <SidePanel open={showDetails} onRequestClose={() => setShowDetails(false)}>
                <EngineDetails motorization={motorization} />
            </SidePanel>
        </>
    );
});

export default memo(Wheeldrive);
